import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, InlineLoading } from '@carbon/react';
import { DocumentDownload, TrashCan, Upload } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { createFileAtom } from 'lib/atoms/file-atom';
import { Sparkline } from 'components/sparkline';
import { formatNumber } from 'utils/number';
import { EscalationFields } from './escalation-fields';
import { SegmentList } from './segment-list';
import { blankEscalation, defaultCurveTailSegments } from './utils';
import {
  extractCurvePoints,
  parseCsv,
  projectCurveTail,
  type ParsedCsv,
} from './curve-utils';
import type {
  CurvePoint,
  CurveSchedule,
  CurveTail,
  EnumOption,
  EscalationConfig,
  StepSegment,
  SupportingFileResponse,
  TailKind,
} from './types';
import styles from './custom-curve-editor.module.scss';

/** How many months past the last imported point to draw as a dashed tail in the sparkline. */
const PROJECTION_MONTHS = 24;

export interface CustomCurveEditorProps {
  curve?: CurveSchedule;
  supportingFile?: SupportingFileResponse | null;
  /** Pre-computed display unit ('$/bbl', '$/MMBtu', '$/gal', '%') for the row. */
  unit: string;
  enums: {
    escalationDirections: EnumOption[];
    escalationPeriods: EnumOption[];
    tailKinds: EnumOption[];
  };
  onChange: (curve: CurveSchedule | undefined) => void;
  onSupportingFileUploaded: (signedId: string | undefined) => void;
}

/**
 * Formats a value with the row's unit for inline display (e.g. tail anchor text,
 * "Hold last value" copy). Percent and dollar treatments diverge — see the mock UI.
 * @param {number} value - The numeric value to display
 * @param {string} unit - The display unit (e.g. "$/bbl", "%")
 * @return {string} The formatted value (e.g. "$-0.45 $/MMBtu", "62%")
 */
function formatValueWithUnit(value: number, unit: string): string {
  if (unit === '%') return `${formatNumber(value, 0, 2)}%`;
  return `$${formatNumber(value, 2, 2)} ${unit}`;
}

/**
 * Drops the day component (`YYYY-MM-DD` → `YYYY-MM`) for inline display.
 * @param {string} yyyymmdd - A first-of-month date string
 * @return {string} The `YYYY-MM` prefix
 */
function formatMonth(yyyymmdd: string): string {
  return yyyymmdd.slice(0, 7);
}

/**
 * Returns a file's extension (including the dot) or a fallback when there isn't one.
 * Used in the wrong-file-type error message.
 * @param {string} name - The filename
 * @return {string} The extension or "(no extension)"
 */
function fileExtension(name: string): string {
  if (!name.includes('.')) return '(no extension)';
  return name.slice(name.lastIndexOf('.'));
}

/**
 * Custom-profile detail panel. Owns the Upload → Parse → Loaded UI states for
 * a single line item's curve, and renders the Tail Behavior sub-section once a
 * curve is loaded. Fully decoupled from any form library — emits the next full
 * CurveSchedule via `onChange` and a fresh signed_id via `onSupportingFileUploaded`.
 * @param {CustomCurveEditorProps} props - The editor props
 * @return {JSX.Element} The custom-curve editor
 */
export function CustomCurveEditor({
  curve,
  supportingFile,
  unit,
  enums,
  onChange,
  onSupportingFileUploaded,
}: CustomCurveEditorProps): JSX.Element {
  const uploadAtom = useMemo(() => createFileAtom(), []);
  const [uploadState, dispatchUpload] = useAtom(uploadAtom);

  const [parseError, setParseError] = useState<string | null>(null);
  const [pendingPicker, setPendingPicker] = useState<ParsedCsv | null>(null);
  const pickedFileRef = useRef<File | null>(null);
  /** Marks which signed_id we've already kicked off parsing for, so the effect doesn't re-parse on subsequent renders. */
  const lastProcessedSignedIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isUploading = uploadState.status === 'uploading';

  /**
   * Commits a parsed curve to form state with a default Hold Flat tail
   * (BE-required) — or surfaces a validation error inline if the column's data
   * doesn't satisfy `extractCurvePoints`.
   * @param {ParsedCsv} parsed - The output of `parseCsv`
   * @param {string} valueColumn - Which of `parsed.numericColumns` the caller picked
   * @return {void}
   */
  const commitCurve = (parsed: ParsedCsv, valueColumn: string): void => {
    const extracted = extractCurvePoints(parsed, valueColumn);
    if ('error' in extracted) {
      setParseError(extracted.error);
      return;
    }
    onChange({
      source: parsed.source,
      valueColumn,
      points: extracted.points,
      // Preserve prior tail config if the user is replacing a curve (so the Hold
      // Flat default doesn't wipe their already-configured tail kind).
      tail: curve?.tail ?? { kind: 'hold_flat' },
    });
    setPendingPicker(null);
  };

  // Watch upload state. On 'complete' (a new signed_id we haven't processed),
  // notify the parent and parse the cached File. On 'error', surface inline.
  // The `cancelled` flag prevents the in-flight `file.text()` promise from
  // resurrecting form state if the user clears, switches profile away from
  // custom, or removes the line item before parsing finishes.
  useEffect(() => {
    if (uploadState.status === 'error') {
      setParseError(uploadState.error ?? 'Failed to upload file.');
      lastProcessedSignedIdRef.current = null;
      return undefined;
    }
    if (uploadState.status !== 'complete' || !uploadState.file)
      return undefined;
    if (lastProcessedSignedIdRef.current === uploadState.file) return undefined;
    lastProcessedSignedIdRef.current = uploadState.file;

    onSupportingFileUploaded(uploadState.file);

    const file = pickedFileRef.current;
    if (!file) return undefined;
    let cancelled = false;
    file
      .text()
      .then((text) => {
        if (cancelled) return;
        const result = parseCsv(text, file.name);
        if ('error' in result) {
          setParseError(result.error);
          return;
        }
        if (result.numericColumns.length === 1) {
          commitCurve(result, result.numericColumns[0]);
        } else {
          setPendingPicker(result);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setParseError(`Could not read "${file.name}".`);
      });
    return () => {
      cancelled = true;
    };
    // commitCurve / onSupportingFileUploaded are recreated each render; the
    // `cancelled` flag scopes side effects to the live closure so omitting
    // them from deps is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadState.status, uploadState.file]);

  /**
   * Pre-upload extension gate + dispatch. Triggered from the hidden file input.
   * Rejects non-CSV files inline (no upload), otherwise stashes the File in a
   * ref and kicks off DirectUpload.
   * @param {File} file - The file the user selected
   * @return {void}
   */
  const handleFileChosen = (file: File): void => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setParseError(
        `Only CSV files are accepted. ${file.name} looks like ${fileExtension(
          file.name
        )}.`
      );
      return;
    }
    setParseError(null);
    setPendingPicker(null);
    pickedFileRef.current = file;
    lastProcessedSignedIdRef.current = null;
    dispatchUpload(file);
  };

  const triggerFilePicker = (): void => {
    fileInputRef.current?.click();
  };

  const handleClear = (): void => {
    onChange(undefined);
    onSupportingFileUploaded(undefined);
    setParseError(null);
    setPendingPicker(null);
    pickedFileRef.current = null;
    lastProcessedSignedIdRef.current = null;
    dispatchUpload(undefined);
  };

  const handleCancelPicker = (): void => {
    setPendingPicker(null);
  };

  // ── Tail handlers ──
  const lastPoint: CurvePoint | undefined =
    curve?.points[curve.points.length - 1];
  const lastValue: number = lastPoint?.value ?? 0;

  const handleTailKindChange = (kind: TailKind): void => {
    if (!curve) return;
    const nextTail: CurveTail = { ...curve.tail, kind };
    if (kind === 'escalating' && !nextTail.escalation) {
      nextTail.escalation = blankEscalation();
    } else if (kind === 'stepped' && !nextTail.segments) {
      nextTail.segments = defaultCurveTailSegments(lastValue);
    }
    onChange({ ...curve, tail: nextTail });
  };

  const handleTailEscalationChange = (
    patch: Partial<EscalationConfig>
  ): void => {
    if (!curve || curve.tail.kind !== 'escalating') return;
    const next: EscalationConfig = {
      ...(curve.tail.escalation ?? blankEscalation()),
      ...patch,
    };
    onChange({ ...curve, tail: { ...curve.tail, escalation: next } });
  };

  const handleTailSegmentsChange = (segments: StepSegment[]): void => {
    if (!curve || curve.tail.kind !== 'stepped') return;
    onChange({ ...curve, tail: { ...curve.tail, segments } });
  };

  // ── Sparkline data ──
  const projectedPoints = useMemo(() => {
    if (!curve) return [] as CurvePoint[];
    return projectCurveTail(curve, PROJECTION_MONTHS);
  }, [curve]);

  const sparklineSeries = useMemo(() => {
    if (!curve) return { points: [], projected: [], yMin: 0, yMax: 0 };
    let yMin = Infinity;
    let yMax = -Infinity;
    const points = curve.points.map((p, i) => {
      if (p.value < yMin) yMin = p.value;
      if (p.value > yMax) yMax = p.value;
      return { x: i, y: p.value };
    });
    const projected = projectedPoints.map((p, i) => ({
      x: curve.points.length + i,
      y: p.value,
    }));
    return { points, projected, yMin, yMax };
  }, [curve, projectedPoints]);

  // ── Render ──

  // Hidden file input — shared between the Empty-state Upload button and the
  // Loaded-state Replace button. `value = ''` after onChange lets the same
  // file be re-picked.
  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".csv"
      className={styles.hiddenInput}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileChosen(file);
        e.target.value = '';
      }}
    />
  );

  // Column picker — multi-numeric-column CSV awaiting user selection.
  if (pendingPicker) {
    return (
      <>
        {hiddenInput}
        <div className={styles.pickerMessage}>
          <strong>{pendingPicker.source}</strong> has{' '}
          {pendingPicker.numericColumns.length} numeric columns. Pick the one to
          use:
        </div>
        <div className={styles.pickerControls}>
          <div className={styles.pickerDropdown}>
            <Dropdown
              id="curve-column-picker"
              label="Select column..."
              titleText=""
              hideLabel
              size="sm"
              items={pendingPicker.numericColumns}
              selectedItem={null}
              onChange={({ selectedItem }: { selectedItem: string | null }) => {
                if (selectedItem) commitCurve(pendingPicker, selectedItem);
              }}
              itemToString={(i: string | null) => i ?? ''}
            />
          </div>
          <Button kind="ghost" size="sm" onClick={handleCancelPicker}>
            Cancel
          </Button>
        </div>
      </>
    );
  }

  // Loaded state — curve is present.
  if (curve && curve.points.length > 0) {
    const firstDate = curve.points[0].date;
    const lastDate = curve.points[curve.points.length - 1].date;
    const dateRange =
      firstDate === lastDate ? firstDate : `${firstDate} → ${lastDate}`;
    const { yMin, yMax } = sparklineSeries;
    const filenameIsServerLinked =
      !!supportingFile?.attachmentHref &&
      supportingFile.fileName === curve.source;

    return (
      <>
        {hiddenInput}

        <div className={styles.metaBlock}>
          <div className={styles.metaLine}>
            <DocumentDownload size={16} />
            {filenameIsServerLinked && supportingFile ? (
              <a
                className={styles.metaFilenameLink}
                href={supportingFile.attachmentHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {curve.source}
              </a>
            ) : (
              <strong className={styles.metaFilename}>
                {curve.source ?? 'Imported curve'}
              </strong>
            )}
            {curve.valueColumn && (
              <span className={styles.metaTag}>
                column: {curve.valueColumn}
              </span>
            )}
          </div>
          <div className={styles.metaStats}>
            {formatNumber(curve.points.length, 0)} points · range {dateRange} ·
            values {formatNumber(yMin, 2, 2)} to {formatNumber(yMax, 2, 2)}{' '}
            {unit}
          </div>
        </div>

        <Sparkline
          points={sparklineSeries.points}
          projectedPoints={sparklineSeries.projected}
          ariaLabel={`Curve: ${curve.points.length} points from ${firstDate} to ${lastDate}`}
        />

        <div className={styles.actions}>
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Upload}
            onClick={triggerFilePicker}
          >
            Replace
          </Button>
          <Button
            kind="danger--ghost"
            size="sm"
            renderIcon={TrashCan}
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>

        {/* Tail behavior */}
        <div className={styles.tailSection}>
          <div className={styles.tailHeader}>
            <span className={styles.tailHeaderLabel}>
              After {formatMonth(lastDate)}:
            </span>
            <div className={styles.tailKindField}>
              <Dropdown
                id="curve-tail-kind"
                label="Tail behavior"
                titleText=""
                hideLabel
                size="sm"
                items={enums.tailKinds}
                selectedItem={
                  enums.tailKinds.find((o) => o.value === curve.tail.kind) ??
                  null
                }
                itemToString={(i: EnumOption | null) => {
                  return i ? tailKindLabel(i) : '';
                }}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: EnumOption | null;
                }) => {
                  if (!selectedItem) return;
                  handleTailKindChange(selectedItem.value as TailKind);
                }}
              />
            </div>
          </div>

          {curve.tail.kind === 'hold_flat' && (
            <div className={styles.tailBody}>
              Hold last value ({formatValueWithUnit(lastValue, unit)})
              indefinitely.
            </div>
          )}

          {curve.tail.kind === 'escalating' && curve.tail.escalation && (
            <EscalationFields
              escalation={curve.tail.escalation}
              enums={{
                escalationDirections: enums.escalationDirections,
                escalationPeriods: enums.escalationPeriods,
              }}
              anchorText={`Starting from ${formatValueWithUnit(
                lastValue,
                unit
              )},`}
              idPrefix="curve-tail-esc"
              onChange={handleTailEscalationChange}
            />
          )}

          {curve.tail.kind === 'stepped' && curve.tail.segments && (
            <SegmentList
              segments={curve.tail.segments}
              unit={unit}
              anchorValue={lastValue}
              idPrefix="curve-tail-seg"
              onChange={handleTailSegmentsChange}
            />
          )}
        </div>

        {parseError && <div className={styles.error}>{parseError}</div>}
      </>
    );
  }

  // Uploading state — file picked, DirectUpload in flight.
  if (isUploading) {
    return (
      <>
        {hiddenInput}
        <InlineLoading
          description={`Uploading ${uploadState.name ?? 'CSV'}...`}
        />
      </>
    );
  }

  // Empty state (also re-used for "post-clear" and "post-error" — the Upload
  // button + hint stays visible).
  return (
    <>
      {hiddenInput}
      <div className={styles.emptyRow}>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Upload}
          onClick={triggerFilePicker}
        >
          Upload CSV
        </Button>
        <span className={styles.emptyHint}>
          Expected columns: <code>date</code>, <code>value</code> (supports{' '}
          <code>YYYY-MM</code>, <code>Mar-26</code>, <code>3/2026</code>).
        </span>
      </div>
      {parseError && <div className={styles.error}>{parseError}</div>}
    </>
  );
}

/**
 * FE-side label override: BE returns "Escalating", but the top-level Profile
 * dropdown shows "Compounded" — keep tail labels consistent.
 * @param {EnumOption} option - The tail-kind enum option from BE
 * @return {string} The display label
 */
function tailKindLabel(option: EnumOption): string {
  if (option.value === 'escalating') return 'Compounded';
  return option.label;
}
